// Higher-order component (HOC) for session management and authentication
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  addConfig,
  type ApplicationConfig,
  setGlobalError,
} from '@/app/coreSlice';
import routes from '@/app/routes';

import useRouterQuery from '@/utils/routerQuery';
import { MicrofrontendDetector } from '@/utils/MicroFrontendDetector';
import Loader from '@/components/loader';

import {
  beginSession,
  clearUserData,
  signIn,
  setUser,
  setUserProfile,
  setAppInitialised,
  setIsAuthenticated,
  signOut,
} from '../store/sessionSlice';
import appLocalStorage from '../store/appLocalStorage';
import { REQUEST_AUTH_TOKEN, HOST_AUTH_TOKEN } from '../utils/constants';
import {
  selectIsAppInitialised,
  selectSessionLoading,
  selectSessionUser,
  selectUserRejected,
} from '../store/session.selector';
import type { User } from '../types/session.types';
import { selectGlobalError } from '@/app/selector';

// withSession HOC wraps a component and injects session/auth logic
const withSession =
  (WrappedComponent: React.ComponentType<any>) => (props: any) => {
    const query = useRouterQuery();
    const navigate = useNavigate();
    const appInitialised: boolean = useAppSelector(selectIsAppInitialised);
    const loading: boolean = useAppSelector(selectSessionLoading);
    const user: User | null = useAppSelector(selectSessionUser);
    const userRejected: boolean = useAppSelector(selectUserRejected);
    const globalError = useAppSelector(selectGlobalError);
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Extract config from query params (region, site, classification)
      const config: ApplicationConfig = {
        region: query.get('region') || '',
        // Example of production unit Sluisklil AmmoniaE
        productionUnit: query.get('site') || 'YARA.SLU.AmmoniaE',
        classification: query.get('classification') || '',
      };
      console.log('withSession - Retrieved config from query params:', config);
      if (MicrofrontendDetector.isMicrofrontend()) {
        // Microfrontend: listen for host messages to receive auth token
        const portfolioPortalUrl = import.meta.env
          .VITE_APP_PORTFOLIO_PORTAL_URL;
        const handleHostMessage = (event: MessageEvent) => {
          // Verify the origin is from the host
          if (event.origin !== portfolioPortalUrl) {
            return;
          }
          if (event.data.type === HOST_AUTH_TOKEN) {
            // Received token and user info from host
            const { token, productionUnit, user } = event.data;
            appLocalStorage.setTokenData(token);
            appLocalStorage.setProductionUnit(productionUnit);
            appLocalStorage.setAccessUserProfile(user);
            const userNames = jwtDecode(token.id_token);
            const userData = {
              email: (userNames as any).email,
              givenName: (userNames as any).given_name,
              familyName: (userNames as any).family_name,
            };
            // Update Redux state
            dispatch(setUser(userData));
            dispatch(setUserProfile(user));
            dispatch(setAppInitialised(true));

            // Update config
            dispatch(
              addConfig({
                region: config.region,
                productionUnit: productionUnit,
                classification: config.classification,
              })
            );
          }
        };

        // Add message listener for host communication
        window.addEventListener('message', handleHostMessage);

        console.log('[STM] portfolioPortalUrl =', portfolioPortalUrl);
        console.log('[STM] window.location.origin =', window.location.origin);
        console.log('[STM] window.parent.origin (expected portal)');

        const targetOrigin =
          portfolioPortalUrl && portfolioPortalUrl !== window.location.origin
            ? portfolioPortalUrl
            : '*';
        // Request auth token from host if in iframe
        if (window.parent !== window) {
          window.parent.postMessage(
            { type: REQUEST_AUTH_TOKEN, app: 'soft-tag-manager' },
            targetOrigin
          );
        }

        // Cleanup: remove event listener on unmount
        return () => {
          window.removeEventListener('message', handleHostMessage);
        };
      } else if (config.productionUnit) {
        // Standalone: begin session using production unit
        console.log('Beginning session for site:', config.productionUnit);
        dispatch(beginSession(query.get('code')))
          .unwrap()
          .then(() => {
            dispatch(setIsAuthenticated());
            // Optionally load user profile here
            appLocalStorage.setProductionUnit(config.productionUnit);
            dispatch(addConfig(config));
          })
          .catch(error => {
            console.error('Error during session initialization:', error);
            signOutHandler();
            // dispatch(setGlobalError({ message: 'Failed to initialize session' }));
          });
      }
    }, []);

    useEffect(() => {
      // If user is rejected, set global error and clear user data
      if (user && userRejected) {
        dispatch(
          setGlobalError({
            message: 'There was a problem validating your user credentials',
          })
        );
        const site = appLocalStorage.getProductionUnit();
        dispatch(clearUserData());
        if (site) {
          appLocalStorage.setProductionUnit(site);
        }
      }
    }, [userRejected, user]);

    useEffect(() => {
      // Navigate to error page if global error is set
      if (globalError) {
        navigate(routes.ERROR);
      }
    }, [globalError]);

    // Handler for signing out user
    const signOutHandler = () => {
      dispatch(clearUserData());
      dispatch(signOut());
      navigate({
        pathname: routes.LOGIN,
      });
      // window.location.reload();
    };

    // Handler for signing in user
    const signInHandler = () => {
      dispatch(signIn());
    };

    // Show loader while app is initializing or session is loading
    if (!appInitialised || loading) {
      return <Loader />;
    }
    // Render wrapped component with injected session props
    return (
      <WrappedComponent
        user={user}
        signIn={signInHandler}
        signOut={signOutHandler}
        {...props}
      />
    );
  };

export default withSession;
